import { DateOnly } from '@if/core/date'
import { EPaymentFrequency } from '@if/core/types'
import { Alert, Grid, Stack } from '@mui/material'
import { BaseWorksheetFormConfig, WorksheetError } from '@src/components/WorksheetForm'
import { computeTotalCoverageFromFunding } from '@src/services/WorksheetComputer'
import { FormEventHandler, useCallback, useMemo, useState } from 'react'
import { Control } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { CalculatorValues } from '../calculatorSchema'
import { CalculatorComputeResult } from '../hooks/useCalculatorCompute'
import CalculatorParametersForm from './CalculatorParametersForm'
import CalculatorPaymentDisplay from './CalculatorPaymentDisplay'
import CalculatorResultSummary from './CalculatorResultSummary'

interface CalculatorFormProps {
  control: Control<CalculatorValues>
  config: BaseWorksheetFormConfig
  result: CalculatorComputeResult | null
  submitErrors: WorksheetError[]
  isComputing: boolean
  interestRate: number
  expiresOn: Date | string
  // When provided, the content is wrapped in a <form> and this handler runs on
  // submit. Omit when the caller drives submission externally (e.g. a stepper
  // owns the form and triggers compute from its own button).
  onSubmit?: FormEventHandler<HTMLFormElement>
  hideSubmitButton?: boolean
  isEstimate?: boolean
}

const CalculatorForm = ({
  control,
  config,
  result,
  submitErrors,
  isComputing,
  interestRate,
  expiresOn,
  onSubmit,
  hideSubmitButton = false,
  isEstimate = false,
}: CalculatorFormProps) => {
  const { t } = useTranslation()
  const expiresOnDate = useMemo(() => new DateOnly(expiresOn), [expiresOn])

  const [isFieldsLoading, setIsFieldsLoading] = useState(false)
  const [formErrors, setFormErrors] = useState<WorksheetError[]>([])

  const handleError = useCallback((errors: WorksheetError[]) => setFormErrors(errors), [])
  const { hasError, errorDisplay } = useMemo(() => {
    const hasError = formErrors.length > 0 || submitErrors.length > 0
    const errorDisplay = [...formErrors, ...submitErrors].map((e) => (
      <Alert key={e.code} severity="error">
        {t(e.messageKey, e.messageArgs) as string}
      </Alert>
    ))
    return { hasError, errorDisplay }
  }, [formErrors, submitErrors, t])

  const submittedValues = result?.values ?? null
  const computedInfo = result?.info ?? null

  const totalCoverage = computeTotalCoverageFromFunding(computedInfo ?? undefined)
  const totalObligation = result
    ? Number(result.values.amountRequested) +
      (computedInfo?.totalInterestAmount ?? 0) +
      (computedInfo?.lenderFee ?? 0) +
      totalCoverage
    : 0

  const monthlyEquivalent = useMemo(() => {
    if (!result || !computedInfo || hasError) return null
    const amount = computedInfo.paymentForFrequency ?? 0
    switch (result.values.paymentFrequency) {
      case EPaymentFrequency.Weekly:
        return amount * (52 / 12)
      case EPaymentFrequency.BiWeekly:
        return amount * (26 / 12)
      default:
        return amount
    }
  }, [result, computedInfo, hasError])

  const body = (
    <Stack spacing={3}>
      {hasError && errorDisplay}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <CalculatorParametersForm
            control={control}
            config={config}
            activationMaxDate={expiresOnDate}
            isComputing={isComputing}
            isFieldsLoading={isFieldsLoading}
            onLoadingChange={setIsFieldsLoading}
            onError={handleError}
            hideSubmitButton={hideSubmitButton}
          />
        </Grid>

        <Grid size={{ xs: 12, lg: 4 }}>
          <CalculatorPaymentDisplay
            isComputing={isComputing}
            monthlyEquivalent={monthlyEquivalent}
            showProtectionPlanNote={!!result && !!submittedValues?.includeProtection}
            isEstimate={isEstimate}
          />
        </Grid>
      </Grid>

      <CalculatorResultSummary
        isComputing={isComputing}
        computedInfo={computedInfo}
        submittedValues={submittedValues}
        interestRate={interestRate}
        totalCoverage={totalCoverage}
        totalObligation={totalObligation}
      />
    </Stack>
  )

  if (onSubmit) {
    return (
      <form onSubmit={onSubmit} noValidate>
        {body}
      </form>
    )
  }
  return <form>{body}</form>
}

export default CalculatorForm
